import {
  closestCenter,
  KeyboardSensor,
  MouseSensor,
  pointerWithin,
  TouchSensor,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import { sortableKeyboardCoordinates, type SortingStrategy } from '@dnd-kit/sortable'
import { useRef, useState } from 'react'
import { createWidget, moveItem } from '../layout/model'
import type { WidgetConfig, WidgetType } from '../types'
import type { LayoutEditor } from './useLayoutEditor'

export const CANVAS_DROP_ID = 'dashboard-canvas'
export const LIBRARY_PREFIX = 'library:'

export type LibraryDragData = { source: 'library'; type: WidgetType }
export type ActiveDrag = { kind: 'library'; type: WidgetType } | { kind: 'widget'; id: string }

/** Grid hücreleri sürükleme sırasında gerçek sıralama değiştirilerek yeniden akar; dnd-kit'in ayrıca kaydırma uygulamasına gerek yok. */
export const liveReorderStrategy: SortingStrategy = () => null

const collisionDetection: CollisionDetection = (args) => {
  const cells = args.droppableContainers.filter((c) => c.id !== CANVAS_DROP_ID)
  if (!args.pointerCoordinates) return closestCenter({ ...args, droppableContainers: cells })
  const hits = pointerWithin({ ...args, droppableContainers: cells })
  if (hits.length) return hits
  return pointerWithin({ ...args, droppableContainers: args.droppableContainers.filter((c) => c.id === CANVAS_DROP_ID) })
}

const isLibrary = (data: unknown): data is LibraryDragData => (data as LibraryDragData | undefined)?.source === 'library'
const indexOf = (list: WidgetConfig[], id: string) => list.findIndex((w) => w.id === id)

export function useEditorDnd(editor: LayoutEditor) {
  const { setWidgets, select } = editor
  const [active, setActive] = useState<ActiveDrag | null>(null)
  const [pendingId, setPendingId] = useState<string | null>(null)
  const pending = useRef<string | null>(null)
  const lastOver = useRef<string | null>(null)
  const snapshot = useRef<WidgetConfig[]>([])

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  function setPending(id: string | null) {
    pending.current = id
    setPendingId(id)
  }

  function finish() {
    setActive(null)
    setPending(null)
    lastOver.current = null
  }

  function onDragStart({ active: a }: DragStartEvent) {
    snapshot.current = editor.widgets
    lastOver.current = null
    const data = a.data.current
    setActive(isLibrary(data) ? { kind: 'library', type: data.type } : { kind: 'widget', id: String(a.id) })
  }

  function onDragOver({ active: a, over }: DragOverEvent) {
    const fromLibrary = isLibrary(a.data.current)
    if (!over) {
      if (fromLibrary && pending.current) {
        const id = pending.current
        setWidgets((prev) => prev.filter((w) => w.id !== id))
        setPending(null)
      }
      lastOver.current = null
      return
    }
    const overId = String(over.id)

    if (fromLibrary && !pending.current) {
      const widget = createWidget((a.data.current as LibraryDragData).type)
      setWidgets((prev) => {
        const next = prev.slice()
        const at = overId === CANVAS_DROP_ID ? next.length : Math.max(indexOf(next, overId), 0)
        next.splice(at, 0, widget)
        return next
      })
      setPending(widget.id)
      lastOver.current = overId
      return
    }

    const movingId = fromLibrary ? pending.current! : String(a.id)
    if (overId === movingId) {
      lastOver.current = movingId
      return
    }
    if (overId === CANVAS_DROP_ID || overId === lastOver.current) return
    lastOver.current = overId
    setWidgets((prev) => moveItem(prev, indexOf(prev, movingId), indexOf(prev, overId)))
  }

  function onDragEnd({ active: a, over }: DragEndEvent) {
    if (isLibrary(a.data.current)) {
      if (over && pending.current) select(pending.current)
      else setWidgets(snapshot.current)
    }
    finish()
  }

  function onDragCancel() {
    setWidgets(snapshot.current)
    finish()
  }

  return {
    sensors,
    collisionDetection,
    active,
    placeholderId: active?.kind === 'widget' ? active.id : pendingId,
    handlers: { onDragStart, onDragOver, onDragEnd, onDragCancel },
  }
}
